import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, withStyles } from '@material-ui/core';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import Select, { components } from 'react-select';

import CustomDialogTitle from '../../partials/customDialogTitle/CustomDialogTitle';
import DraggableDialog from '../../partials/DraggableDialog';
import styles from './styles';
import { actions as reduxActions } from './reducers';
import { reactSelectDefaultStyles } from '../../consts/reactSelectDefaultStyles';
import { translate } from '../../i18n/I18nProvider';
import Optional from '../../utils/optional';
import { isUndefined } from '../../utils/isDefined';
import createSelectClientOptions from '../../partials/selectClient/actions/createSelectClientOptions';
import ActionsButtons from './components/ActionsButtons';
import clsx from 'clsx';
import InfoBox from './components/InfoBox';
import moveRange from './actions/moveRange';
import { actions as numberRangereduxActions } from '../../pages/externalNumberRange/reducers';
import { getExternalNoRanges } from '../../pages/externalNumberRange/actions/getExternalNoRanges';

const MoveNumberRange = ({
  classes,
  open,
  closeMoveRange,
  selectOptions,
  ranges,
  refreshRangeTable,
  fromBE,
  resetValues,
  getExternalNoRanges
}) => {
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [rangeStatuses, setRangeStatuses] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);

  const clientOption = useMemo(() => createSelectClientOptions(selectOptions), [
    selectOptions
  ]);

  useEffect(() => {
    if (!open) {
      setSelectedClientId(null);
      setRangeStatuses({});
      setIsProcessing(false);
      setHasMoved(false);
    }
  }, [open]);

  const onMoveClick = useCallback(async () => {
    setHasMoved(true);
    setIsProcessing(true);
    await moveRange({
      rangesIds: ranges.map(range => range.id),
      targetClientId: selectedClientId.value,
      refreshRangeTable,
      onProgress: (rangeId, status, data) => {
        setRangeStatuses(prev => ({
          ...prev,
          [rangeId]: { status, data }
        }));
      }
    });
    if (fromBE) {
      refreshRangeTable(true);
    } else {
      getExternalNoRanges({ ...resetValues });
    }
    setIsProcessing(false);
  }, [
    ranges,
    selectedClientId,
    fromBE,
    refreshRangeTable,
    resetValues,
    getExternalNoRanges
  ]);

  const Option = props => {
    const { children } = props;
    return (
      <components.Option {...props}>
        <div>
          <div>{Optional(children?.name).or('')}</div>
          <div className={classes.accountNo}>
            <div className={classes.platform}>
              {Optional(children?.platform).or('')}
            </div>
            {Optional(children?.notes).or('')}
          </div>
        </div>
      </components.Option>
    );
  };

  const SingleValue = props => {
    const { children } = props;
    return (
      <components.SingleValue {...props}>
        {children?.name}
      </components.SingleValue>
    );
  };

  const customFilter = (option, searchText) => {
    if (
      option.data.label.name.toLowerCase().includes(searchText.toLowerCase()) ||
      option.data.label.notes.toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleChange = useCallback(selectedOption => {
    setSelectedClientId(selectedOption);
  }, []);

  return (
    <Dialog
      maxWidth="lg"
      classes={{
        paper: classes.dialogPaper
      }}
      PaperComponent={DraggableDialog}
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          closeMoveRange();
        }
      }}
    >
      <CustomDialogTitle onCloseClick={closeMoveRange}>
        {translate('NUMBER_RANGES.MOVE_NUMBER_RANGE')}
      </CustomDialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.label}>
          {ranges.map(range => {
            const status = rangeStatuses[range.id]?.status;
            const errorDetail = rangeStatuses[range.id]?.data;
            return (
              <div key={range.id} className={classes.rangeContainer}>
                <div className={classes.rangeTextContainer}>
                  <div className={classes.rangeMainRow}>
                    <i
                      className={clsx('fas fa-circle', classes.icon, {
                        [classes.iconPending]: status === 'pending',
                        [classes.iconSuccess]: status === 'success',
                        [classes.iconError]: status === 'error'
                      })}
                    />
                    {range.range_from} - {range.range_up_to_incl}
                    {status === 'pending' && (
                      <i
                        className={clsx(
                          'fas fa-spinner fa-spin',
                          classes.spinner
                        )}
                      />
                    )}
                    {status === 'success' && (
                      <i
                        className={clsx('fas fa-check', classes.successIcon)}
                      />
                    )}
                    {status === 'error' && (
                      <i className={clsx('fas fa-times', classes.errorIcon)} />
                    )}
                  </div>
                  {status === 'error' && errorDetail && (
                    <div className={classes.errorDetail}>{errorDetail}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {!hasMoved && (
          <div className={classes.bottomSection}>
            <Select
              styles={reactSelectDefaultStyles}
              className={classes.select}
              value={selectedClientId}
              onChange={handleChange}
              options={clientOption.length > 0 ? clientOption : ''}
              placeholder={`${translate('EDIT.SELECT_CLIENT')} ...`}
              components={{ Option, SingleValue }}
              filterOption={customFilter}
              isDisabled={isProcessing}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        )}
        <div className={classes.bottomSection}>
          <InfoBox />
        </div>
      </DialogContent>
      <div className={classes.footer}>
        <ActionsButtons
          onCloseClick={closeMoveRange}
          disabled={isUndefined(selectedClientId) || isProcessing}
          handleSubmit={onMoveClick}
          hasMoved={hasMoved}
        />
      </div>
    </Dialog>
  );
};

const mapStatesToProps = ({ moveNumberRange, selectClient }) => ({
  open: moveNumberRange.open,
  selectOptions: selectClient.selectClientsList,
  ranges: moveNumberRange.ranges,
  fromBE: moveNumberRange.fromBE,
  resetValues: moveNumberRange.resetValues
});

const mapDispatchToProps = {
  closeMoveRange: reduxActions.closeMoveRange,
  refreshRangeTable: numberRangereduxActions.setRefresh,
  getExternalNoRanges
};

export default flowRight(
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(MoveNumberRange);
